import { Locale } from 'vue-i18n';
import {
  _Boolean,
  GetSourceIpQuery,
  GetGatewayServerQuery,
  GetGatewayServerQueryVariables,
  Product,
} from '@/API';
import {
  getMenus,
  GetMenusQuery,
  GetMenusQueryVariables,
} from '@/appsync/graphql/menu';
import {
  graphqlQuery,
  getNameByLang,
  getDescriptionByLang,
  getIcon,
  addParameterToUrl,
} from '@/appsync/service/GraphqlUtils';
import { getGatewayServer, getSourceIp } from '@/graphql/queries';
import {
  ID,
  LoginInfo,
  ProductMenu,
  ProductAdditionalQueryMap,
} from '@/types/type';
import { _BooleanEnum2boolean } from '@/utils/CommonUtils';

const CIDRMatcher = require('cidr-matcher');

export interface FetchMenuQueryParam extends LoginInfo {
  menuIds: ID[];
  language: Locale;
  productAdditionalQueryMap?: ProductAdditionalQueryMap;
}

export const fetchMenus = async (
  param: FetchMenuQueryParam
): Promise<ProductMenu[]> => {
  const menus: ProductMenu[] = [];
  if (param.menuIds.length) {
    const variables: GetMenusQueryVariables = { ...param };
    const result = await graphqlQuery<GetMenusQuery, GetMenusQueryVariables>(
      getMenus(param.menuIds),
      variables
    );
    if (result) {
      for (let menu of Object.values(result)) {
        if (menu) {
          const title = getNameByLang(menu.menuNames.items, param.language);
          const description = getDescriptionByLang(
            menu.menuDescriptions.items,
            param.language
          );
          const product = menu.product;
          const url = addParameterToUrl(
            menu.url,
            product,
            param.productAdditionalQueryMap
          );
          const canUseFromExtra = _BooleanEnum2boolean(menu.canUseFromExtra);
          menus.push({
            id: menu.menuId,
            title,
            menuType: 'menu',
            product,
            url,
            description,
            available: true,
            isFavorite: true,
            icon: getIcon(menu.menuIconFont),
            canUseFromExtra,
          });
        }
      }
    }
  }
  return menus;
};

export interface FetchAllMenuQueryParam extends LoginInfo {
  favoriteMenuIds: ID[];
  historyMenuIds: ID[];
  language: Locale;
  productAdditionalQueryMap?: ProductAdditionalQueryMap;
}

export const fetchSourceIp = async (): Promise<string[] | null> => {
  const result = await graphqlQuery<GetSourceIpQuery, {}>(getSourceIp);
  if (result && result.getSourceIp && result.getSourceIp.xForwardedFor) {
    return result.getSourceIp.xForwardedFor.split(',').map(ip => ip.trim());
  }
  return null;
};

export const isInHouseIP = async (
  tenant: string,
  landscape: string,
  product: Product
): Promise<boolean> => {
  const userIps: string[] | null = await fetchSourceIp();
  const result = await graphqlQuery<
    GetGatewayServerQuery,
    GetGatewayServerQueryVariables
  >(getGatewayServer, { tenant, landscape, product });

  if (
    result &&
    result.getGatewayServer &&
    _BooleanEnum2boolean(result.getGatewayServer.useInternetAccess)
  ) {
    const includesResult: boolean = matchCidrs(
      result.getGatewayServer.gatewayServerIps == null
        ? null
        : result.getGatewayServer.gatewayServerIps,
      userIps
    );

    return _BooleanEnum2boolean(result.getGatewayServer.isInternalGateWayServer)
      ? includesResult
      : !includesResult;
  }
  return true;
};

export const matchCidrs = (
  gatewayServerIps: (string | null)[] | null,
  userIps: string[] | null
): boolean => {
  if (!gatewayServerIps) {
    return false;
  }
  const gatewayServerCidrs: string[] = [];

  gatewayServerIps.forEach(ip => {
    if (!ip) {
      return;
    }
    const cidr = ip.includes('/') ? ip : `${ip}/32`;
    gatewayServerCidrs.push(cidr);
  });

  const matcher = new CIDRMatcher(gatewayServerCidrs);
  return matcher.containsAny(userIps);
};
